
import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import { Card } from '../components/shared/Card';
import { ShieldCheck, TrendingUp, MapPin, Home, Award, Sparkles, Building2 } from 'lucide-react';
import { Button } from '../components/shared/Button';
import { SocialEcosystem } from '../components/shared/SocialEcosystem';
import { RoadmapSection } from '../components/shared/RoadmapSection';
import { FreeTrialButton } from '../components/shared/FreeTrialButton';
import { ConsultationSection } from '../components/shared/ConsultationSection';
import { EXTERNAL_LINKS } from '../constants';

export const RealEstatePage: React.FC = () => {
  const { t } = useLanguage();

  return (
    <div className="bg-[#0B1120] text-white min-h-screen pt-32 pb-20">

      {/* 1. HERO SECTION: Impatto Visivo & Stato "Coming Soon" */}
      <section className="mb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
              <h1 className="text-3xl sm:text-5xl md:text-7xl font-black uppercase tracking-tight text-white leading-tight">
                {t('realEstate.heroTitle')}
              </h1>
              <div className="inline-flex items-center bg-surface-dark border border-gray-700 px-4 py-2 rounded-full gap-2 self-start md:self-auto">
                <Sparkles className="w-4 h-4 text-orange-500" />
                <span className="text-[10px] sm:text-xs font-bold text-gray-300 uppercase tracking-widest">{t('realEstate.comingSoon')}</span>
              </div>
            </div>

            <p className="text-xl text-gray-400 max-w-3xl leading-relaxed mb-8">
              {t('realEstate.heroDesc')}
            </p>


            <div className="relative z-[100] overflow-visible">
              <FreeTrialButton
                serviceName="Divisione Immobiliare"
                buttonText={t('realEstate.requestInfoBtn')}
              />
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 2. VISION & WHY: Il "Perché" dell'Immobiliare */}
      <section className="mb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="bg-[#151E32] border border-gray-800 rounded-3xl p-12 text-center relative overflow-hidden shadow-2xl">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-600 via-orange-400 to-orange-600"></div>

              <div className="mb-6 inline-flex items-center justify-center">
                <Building2 size={48} className="text-orange-500" />
              </div>

              <h2 className="text-3xl font-black text-white uppercase mb-6 tracking-wide">
                {t('realEstate.vision.title')}
              </h2>

              <p className="text-gray-300 leading-relaxed mb-12">
                {t('realEstate.vision.description')}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 border-t border-gray-800 pt-10">
                <div>
                  <div className="text-2xl sm:text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-200 to-orange-500 mb-2 leading-tight">
                    {t('realEstate.vision.stats.transparency')}
                  </div>
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                    {t('realEstate.vision.stats.transparencyLabel')}
                  </div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-2 leading-tight">
                    {t('realEstate.vision.stats.longTerm')}
                  </div>
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                    {t('realEstate.vision.stats.longTermLabel')}
                  </div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-2 leading-tight">
                    {t('realEstate.vision.stats.global')}
                  </div>
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                    {t('realEstate.vision.stats.globalLabel')}
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 3. STRATEGY & PILLARS: Il "Come" */}
      <section className="mb-24 bg-[#080C14] py-20 border-y border-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black uppercase tracking-wide mb-6">
              {t('realEstate.pillars.title')} <span className="text-orange-500">{t('realEstate.pillars.titlePart2')}</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              {t('realEstate.pillars.subtitle')}
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            {/* Cards */}
            <AnimatedSection delay={100}>
              <div className="bg-[#151E32] border border-gray-800 rounded-2xl p-8 hover:border-orange-500/50 transition-colors h-full">
                <div className="w-12 h-12 rounded-xl bg-orange-900/30 flex items-center justify-center text-orange-500 mb-6 border border-orange-500/20">
                  <Home size={24} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{t('realEstate.pillars.cards.solidity')}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  {t('realEstate.pillars.cards.solidityDesc')}
                </p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <div className="bg-[#151E32] border border-gray-800 rounded-2xl p-8 hover:border-orange-500/50 transition-colors h-full">
                <div className="w-12 h-12 rounded-xl bg-orange-900/30 flex items-center justify-center text-orange-500 mb-6 border border-orange-500/20">
                  <MapPin size={24} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{t('realEstate.pillars.cards.strategic')}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  {t('realEstate.pillars.cards.strategicDesc')}
                </p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={300}>
              <div className="bg-[#151E32] border border-gray-800 rounded-2xl p-8 hover:border-orange-500/50 transition-colors h-full">
                <div className="w-12 h-12 rounded-xl bg-orange-900/30 flex items-center justify-center text-orange-500 mb-6 border border-orange-500/20">
                  <Award size={24} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{t('realEstate.pillars.cards.ecosystem')}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  {t('realEstate.pillars.cards.ecosystemDesc')}
                </p>
              </div>
            </AnimatedSection>
          </div>

          {/* Detailed Strategy Split */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <AnimatedSection>
              <h2 className="text-3xl font-black uppercase tracking-wide mb-8 text-white">
                {t('realEstate.strategy.title')} <span className="text-orange-500">{t('realEstate.strategy.titlePart2')}</span>
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-surface-dark flex-shrink-0 flex items-center justify-center text-blue-400 border border-gray-800">
                    <TrendingUp size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-white mb-1">{t('realEstate.strategy.items.growth')}</h4>
                    <p className="text-xs text-gray-500">{t('realEstate.strategy.items.growthDesc')}</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-surface-dark flex-shrink-0 flex items-center justify-center text-orange-500 border border-gray-800">
                    <ShieldCheck size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-white mb-1">{t('realEstate.strategy.items.risk')}</h4>
                    <p className="text-xs text-gray-500">{t('realEstate.strategy.items.riskDesc')}</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-surface-dark flex-shrink-0 flex items-center justify-center text-gray-400 border border-gray-800">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-white mb-1">{t('realEstate.strategy.items.positions')}</h4>
                    <p className="text-xs text-gray-500">{t('realEstate.strategy.items.positionsDesc')}</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <div className="bg-[#151E32] border border-gray-800 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-white mb-8">{t('realEstate.strategy.portfolio.title')}</h3>
                <div className="space-y-6 mb-8">
                  <div>
                    <div className="flex justify-between text-sm font-bold text-gray-300 mb-2">
                      <span>{t('realEstate.strategy.portfolio.residential')}</span>
                      <span>45%</span>
                    </div>
                    <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div className="h-full bg-orange-500 w-[45%] rounded-full"></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm font-bold text-gray-300 mb-2">
                      <span>{t('realEstate.strategy.portfolio.commercial')}</span>
                      <span>35%</span>
                    </div>
                    <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div className="h-full bg-orange-500 w-[35%] rounded-full"></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm font-bold text-gray-300 mb-2">
                      <span>{t('realEstate.strategy.portfolio.development')}</span>
                      <span>20%</span>
                    </div>
                    <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div className="h-full bg-orange-500 w-[20%] rounded-full"></div>
                    </div>
                  </div>
                </div>
                <div className="h-px bg-gray-800 w-full mb-6"></div>
                <a href={EXTERNAL_LINKS.TELEGRAM_ACCESS} target="_blank" rel="noopener noreferrer" className="block w-full">
                  <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold border-0 shadow-lg shadow-orange-500/20 py-3">
                    {t('realEstate.strategy.portfolio.button')}
                  </Button>
                </a>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CONSULTATION */}
      <ConsultationSection preselectedService="Divisione Immobiliare" />

      {/* 4. ROADMAP: Il "Quando" */}
      <RoadmapSection category="realEstate" showFullLink={true} />

      {/* 5. SOCIAL */}
      <section className="py-16 bg-[#080C14] border-t border-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <SocialEcosystem />
          </AnimatedSection>
        </div>
      </section>

    </div>
  );
};
